import {
  useId,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { cn } from "../lib/cn";
import { FieldLabel } from "./FieldLabel";

const SLOT_COUNT = 6;
const SLOT_INDEXES = [0, 1, 2, 3, 4, 5];

interface OtpInputProps {
  /** Controlled value: digits only, at most 6 (the component enforces both). */
  value: string;
  /** Receives the already-filtered digits (`/\D/` stripped, sliced to 6). */
  onChange: (value: string) => void;
  /**
   * Fires when the sixth digit lands, from the same change event, with the
   * value passed directly because state hasn't updated yet. Screens wire their
   * `verify()` here and keep their own in-flight guards.
   */
  onComplete?: ((value: string) => void) | undefined;
  disabled?: boolean | undefined;
  /** Marks the code invalid; screens render their own error message. */
  error?: boolean | undefined;
  /** Focuses the real input on mount. Defaults to `true`; pass `false` to opt out. */
  autoFocus?: boolean | undefined;
  /** Visible + accessible label for the single real input. */
  label?: string | undefined;
}

/**
 * Segmented one-time-code input, shared by the OTP screens.
 *
 * It follows the `input-otp` pattern: one real `<input>` receives every event
 * while `aria-hidden` slot boxes render the digits. The input is stretched
 * over the slot row at opacity 0, not `display: none`, so it stays focusable
 * and autofillable. A single input keeps iOS/Android SMS autofill
 * (`autoComplete="one-time-code"`) and backspace native, and paste is one
 * handler on one element, with no per-slot focus to manage. The slot layer
 * disables pointer events, so tapping any slot focuses the input. The browser
 * caret is hidden (`caret-transparent`) and the selection is pinned to the
 * end, so typing and backspace always act on the tail; the active slot, where
 * the next digit goes, shows a caret while the input is focused.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  disabled,
  error,
  autoFocus = true,
  label = "Verification code",
}: OtpInputProps): ReactNode {
  const inputId = useId();
  const [focused, setFocused] = useState(false);
  const activeIndex = Math.min(value.length, SLOT_COUNT - 1);

  /** Filter raw entry to ≤6 digits, lift it, and fire `onComplete` on the 6th. */
  function applyEntry(raw: string): void {
    const digits = raw.replace(/\D/g, "").slice(0, SLOT_COUNT);
    const wasComplete = value.length === SLOT_COUNT;
    // Call unconditionally (even when the digits are unchanged, e.g. a filtered
    // keystroke), because screens clear their error message on any entry activity.
    onChange(digits);
    if (digits.length === SLOT_COUNT && !wasComplete) onComplete?.(digits);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    applyEntry(e.target.value);
  }

  /**
   * Paste replaces the value with the clipboard text (the OTP convention).
   * It is handled explicitly because the native path truncates to `maxLength`
   * before the digit filter runs, so pasting "123-456" would keep only "12345".
   */
  function handlePaste(e: ClipboardEvent<HTMLInputElement>): void {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    if (!/\d/.test(text)) return; // nothing usable; keep what's typed
    applyEntry(text);
  }

  /** Keep the selection at the tail so typing/backspace always act on the end. */
  function pinSelectionToEnd(e: SyntheticEvent<HTMLInputElement>): void {
    const el = e.currentTarget;
    el.setSelectionRange(el.value.length, el.value.length);
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>): void {
    setFocused(true);
    pinSelectionToEnd(e);
  }

  return (
    <div>
      <FieldLabel htmlFor={inputId}>{label}</FieldLabel>
      <div className="relative">
        <div aria-hidden="true" className="pointer-events-none flex gap-2">
          {SLOT_INDEXES.map((i) => {
            const digit = value[i] ?? "";
            const isActive = focused && !disabled && i === activeIndex;
            return (
              <div
                key={i}
                className={cn(
                  "flex h-14 w-12 items-center justify-center rounded-2xl border-2 text-xl",
                  "border-border bg-input-bg text-foreground",
                  error && "border-danger/40 bg-danger-bg",
                  isActive && (error ? "border-danger bg-white" : "border-primary bg-white"),
                  disabled && "text-cloud-blue cursor-not-allowed opacity-50",
                )}
              >
                {digit === "" && isActive ? (
                  <span className="bg-foreground h-6 w-0.5 animate-pulse" />
                ) : (
                  digit
                )}
              </div>
            );
          })}
        </div>
        <input
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={SLOT_COUNT}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={error ? true : undefined}
          onChange={handleChange}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onBlur={() => setFocused(false)}
          onSelect={pinSelectionToEnd}
          // A font size of at least 16px keeps iOS from zooming the page on focus.
          className="absolute inset-0 h-full w-full text-[17px] caret-transparent opacity-0 focus:outline-none"
        />
      </div>
    </div>
  );
}
